import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { MessageSquareText, Save, ListChecks, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDateShort } from './shared';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Saisie par période (option A) : observations groupe + actions formateur, agrégées dans le PDF
export const ObservationsPeriodePanel = ({ dossierId, debut, fin, data, onSaved }) => {
  const exacte = data?.observation_periode_exacte;
  const [form, setForm] = useState({ observations_groupe: '', actions_formateur: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm({ observations_groupe: exacte?.observations_groupe || '', actions_formateur: exacte?.actions_formateur || '' }); }, [exacte, debut, fin]);
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/rapports/dossier/${dossierId}/observations-periode`, form, { params: { debut, fin } });
      toast.success('Observations de la période enregistrées'); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de l\'enregistrement'); } finally { setSaving(false); }
  };
  const autres = (data?.observations_periode || []).filter(o => !(o.date_debut === debut && o.date_fin === fin));
  const isSession = debut <= '2000-01-01';
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="observations-periode-panel">
      <Card className="border-0 shadow-sm lg:col-span-2">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><MessageSquareText className="w-4 h-4 text-indigo-500" /> Observations du formateur — période du {fmtDateShort(debut)} au {fmtDateShort(fin)}</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm">
          {isSession && <p className="text-xs text-amber-600">Choisissez une semaine, un mois, un trimestre ou une année pour saisir des observations rattachées à cette période.</p>}
          <div>
            <p className="text-xs font-medium text-slate-600 mb-1">Observations sur le groupe</p>
            <Textarea rows={3} disabled={isSession} value={form.observations_groupe} onChange={(e) => setForm({ ...form, observations_groupe: e.target.value })} placeholder="Dynamique du groupe, climat, points forts, difficultés collectives…" data-testid="obs-groupe-textarea" />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-600 mb-1">Actions formateur / préconisations (texte libre, complète la partie automatique)</p>
            <Textarea rows={2} disabled={isSession} value={form.actions_formateur} onChange={(e) => setForm({ ...form, actions_formateur: e.target.value })} placeholder="Entretiens prévus, remédiations, ajustements pédagogiques…" data-testid="actions-formateur-textarea" />
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-slate-400">{exacte?.updated_at ? `Dernière saisie : ${exacte.auteur_nom || ''} le ${fmtDateShort(exacte.updated_at.slice(0, 10))}` : ''}</span>
            <Button type="button" size="sm" disabled={saving || isSession} onClick={save} className="bg-indigo-600 hover:bg-indigo-700" data-testid="obs-periode-save-btn"><Save className="w-3.5 h-3.5 mr-1" /> Enregistrer</Button>
          </div>
          {autres.length > 0 && (
            <div className="border-t pt-2 space-y-1.5" data-testid="obs-periodes-incluses">
              <p className="text-xs font-medium text-slate-600">Observations des sous-périodes incluses ({autres.length}) — reprises dans le PDF</p>
              {autres.map(o => (
                <div key={o.id} className="text-xs bg-slate-50 rounded-md p-2">
                  <p className="font-semibold text-slate-700">Du {fmtDateShort(o.date_debut)} au {fmtDateShort(o.date_fin)} <span className="font-normal text-slate-400">· {o.auteur_nom}</span></p>
                  {o.observations_groupe && <p className="text-slate-600 whitespace-pre-line">{o.observations_groupe}</p>}
                  {o.actions_formateur && <p className="text-slate-500 whitespace-pre-line mt-1"><b>Actions :</b> {o.actions_formateur}</p>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <div className="space-y-4">
        <Card className="border-0 shadow-sm" data-testid="modules-traites-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BookOpen className="w-4 h-4 text-amber-500" /> Modules traités ({data?.modules_traites?.length || 0})</CardTitle></CardHeader>
          <CardContent className="text-xs space-y-1">
            {(data?.modules_traites || []).map(m => <p key={m.module_id} className="text-slate-700"><span className="font-mono text-slate-500">{m.code}</span> {m.titre} <span className="text-slate-400">· {m.dates.length} séance(s)</span></p>)}
            {!data?.modules_traites?.length && <p className="text-slate-400">Aucun module renseigné sur la période</p>}
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm" data-testid="actions-auto-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><ListChecks className="w-4 h-4 text-red-500" /> Points de vigilance automatiques ({data?.actions_auto?.length || 0})</CardTitle></CardHeader>
          <CardContent className="text-xs space-y-1.5">
            {(data?.actions_auto || []).map((a, i) => <p key={i} className="text-slate-700"><b>{a.candidat || 'Groupe'}</b> — {a.texte}</p>)}
            {!data?.actions_auto?.length && <p className="text-slate-400">Aucun point de vigilance détecté</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
